import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaCogs, FaUsers, FaStar, FaBriefcase, FaChartBar } from "react-icons/fa";
import API from "../api";

const statusColors = { new: "#0ea5e9", read: "#f59e0b", replied: "#10b981", closed: "#6b7280" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/stats").then(({ data }) => { setStats(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const cards = [
    { label: "Total Enquiries", value: stats?.total || 0, icon: <FaEnvelope />, color: "#0ea5e9" },
    { label: "New Leads", value: stats?.newLeads || 0, icon: <FaChartBar />, color: "#8b5cf6" },
    { label: "Replied", value: stats?.replied || 0, icon: <FaCogs />, color: "#10b981" },
    { label: "Closed", value: stats?.closed || 0, icon: <FaBriefcase />, color: "#6b7280" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-white">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-1">Welcome back! Here's what's happening.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card, i) => (
          <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="glass rounded-2xl p-5 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                style={{ background: `${card.color}20`, color: card.color }}>
                {card.icon}
              </div>
            </div>
            <div className="text-3xl font-black text-white">{card.value}</div>
            <div className="text-gray-400 text-xs mt-1">{card.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Enquiries */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <h2 className="text-white font-bold mb-4 flex items-center gap-2">
            <FaEnvelope className="text-blue-400" /> Recent Enquiries
          </h2>
          <div className="space-y-3">
            {stats?.recent?.length === 0 && <p className="text-gray-500 text-sm">No enquiries yet</p>}
            {stats?.recent?.map((c) => (
              <div key={c._id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-white text-sm font-medium">{c.name}</p>
                  <p className="text-gray-400 text-xs">{c.service} • {c.email}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full font-semibold"
                  style={{ background: `${statusColors[c.status]}20`, color: statusColors[c.status] }}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiries by Service */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <h2 className="text-white font-bold mb-4 flex items-center gap-2">
            <FaChartBar className="text-purple-400" /> Enquiries by Service
          </h2>
          <div className="space-y-3">
            {stats?.byService?.length === 0 && <p className="text-gray-500 text-sm">No data yet</p>}
            {stats?.byService?.map((s) => (
              <div key={s._id} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-300 text-xs">{s._id}</span>
                    <span className="text-white text-xs font-bold">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(s.count / (stats?.total || 1)) * 100}%` }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="h-full gradient-bg rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
